/**
 * HNLaunchCountdown — emerald countdown widget for the landing hero.
 *
 * Renders only when /api/hn-launch/config returns state ∈ {countdown, launching}.
 * Self-managing: ticks every second, hits the API once on mount + refresh on visibility change.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Rocket, Calendar, ExternalLink, Twitter, Linkedin, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'cartie_hn_launch_dismissed_v1';

function fmtCountdown(seconds) {
  if (seconds <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return { d, h, m, s };
}

export default function HNLaunchCountdown() {
  const [cfg, setCfg] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);
  const [shareLinks, setShareLinks] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/hn-launch/config`);
      if (r.ok) setCfg(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    setDismissed(localStorage.getItem(DISMISS_KEY) === 'true');
    refresh();
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [refresh]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (cfg?.state === 'launching') {
      fetch(`${API}/api/hn-launch/share-intent`).then(r => r.ok && r.json()).then(d => d && setShareLinks(d)).catch(() => {});
    }
  }, [cfg?.state]);

  if (dismissed) return null;
  if (!cfg || cfg.state === 'none' || cfg.state === 'scheduled_far' || cfg.state === 'launched') return null;

  const launchAt = cfg.launch_at ? new Date(cfg.launch_at) : null;
  const secondsRemaining = launchAt ? Math.max(0, Math.floor((launchAt.getTime() - now) / 1000)) : 0;
  const { d, h, m, s } = fmtCountdown(secondsRemaining);
  const isLive = cfg.state === 'launching';

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  return (
    <div
      className={`relative w-full ${isLive ? 'bg-emerald-600 bg-gradient-to-r from-emerald-600 via-emerald-500 to-cyan-600' : 'bg-slate-900 border-b border-emerald-500/30'} text-white px-4 py-3 z-30`}
      data-testid="hn-launch-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 sm:gap-5">
        <div className="flex items-center gap-2 shrink-0">
          <Rocket className={`w-5 h-5 ${isLive ? 'text-white' : 'text-emerald-400'}`} />
          <span className="font-bold text-sm sm:text-base">
            {isLive ? '🚀 LIVE on Hacker News' : 'Launching on Hacker News'}
          </span>
        </div>

        {!isLive && launchAt && (
          <div className="flex items-center gap-3 text-sm flex-1 min-w-0" data-testid="hn-countdown-timer">
            <div className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-slate-200 text-xs">{launchAt.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
            <div className="font-mono font-bold text-base">
              <span className="text-emerald-300">{d}</span><span className="text-slate-300 mx-0.5 text-xs">d</span>
              <span className="text-emerald-300 ml-1.5">{String(h).padStart(2, '0')}</span><span className="text-slate-300 mx-0.5 text-xs">h</span>
              <span className="text-emerald-300 ml-1.5">{String(m).padStart(2, '0')}</span><span className="text-slate-300 mx-0.5 text-xs">m</span>
              <span className="text-emerald-300 ml-1.5">{String(s).padStart(2, '0')}</span><span className="text-slate-300 mx-0.5 text-xs">s</span>
            </div>
          </div>
        )}

        {isLive && (
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-wrap">
            <span className="text-sm text-emerald-50">{cfg.tagline}</span>
            {cfg.hn_url && (
              <a
                href={cfg.hn_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 bg-white text-emerald-700 hover:bg-emerald-50 font-bold text-sm px-3 py-1 rounded-md transition-colors"
                data-testid="hn-upvote-cta"
              >
                Upvote on HN <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {shareLinks?.twitter_intent_url && (
              <a
                href={shareLinks.twitter_intent_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-900 text-white font-medium text-xs px-2.5 py-1 rounded-md transition-colors"
                data-testid="hn-share-twitter"
              >
                <Twitter className="w-3 h-3" /> Tweet
              </a>
            )}
            {shareLinks?.linkedin_intent_url && (
              <a
                href={shareLinks.linkedin_intent_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-900 text-white font-medium text-xs px-2.5 py-1 rounded-md transition-colors"
                data-testid="hn-share-linkedin"
              >
                <Linkedin className="w-3 h-3" /> Share
              </a>
            )}
          </div>
        )}

        <button
          onClick={dismiss}
          className="ml-auto text-slate-300 hover:text-white shrink-0"
          aria-label="Dismiss launch banner"
          data-testid="hn-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
